import React, { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { SETTING_KEYS } from '../constants';
import { ListItemButton } from "@mui/material";

import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';

import {
  Email,
  Phone,
  Menu as MenuIcon,
} from '@mui/icons-material';
import FacebookSharpIcon from '@mui/icons-material/FacebookSharp';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

interface MenuItem {
  text: string;
  link: string;
  action?: () => void;
}

const Navbar: React.FC = () => {
  const { settings } = useSettings();
  const navigate = useNavigate();

  const getSettingValue = (key: string): string | null => {
    const setting = settings?.find((s) => s.key === key);
    return setting?.value || null;
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const menuItems: MenuItem[] = [
    //{ text: 'Home', link: '/' },
    { text: 'About', link: '/#about-us' },
    { text: 'Services', link: '/#services' },
    { text: 'Contact', link: '/#contact-us' },
    { text: 'Faq', link: '/faq' },
  ];

  const contactNumber = getSettingValue(SETTING_KEYS.CONTACT_NUMBER);
  const email = getSettingValue(SETTING_KEYS.EMAIL_ADDRESS);
  const facebookUrl = getSettingValue(SETTING_KEYS.FACEBOOK);
  const linkedInUrl = getSettingValue(SETTING_KEYS.LINKEDIN);
  const twitterUrl = getSettingValue(SETTING_KEYS.TWITTER);
  const siteLogo = getSettingValue(SETTING_KEYS.SITE_LOGO);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#1976d2',
          boxShadow: 2,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Box display="flex" alignItems="center">
            <IconButton
              edge="start"
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
              color="inherit"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            {siteLogo ? (
              <Box
                component="img"
                src={siteLogo}
                alt="Site Logo"
                sx={{
                  width: 150,
                  height: 50,
                  borderRadius: 2,
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Typography 
                variant="h5" 
                sx={{ 
                  cursor: 'pointer', 
                  fontFamily: "'Lobster', cursive", 
                  fontWeight: 700,
                  minWidth: 220,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
                onClick={() => navigate('/')}
              >
                {getSettingValue(SETTING_KEYS.SITE_NAME) || 'COMPANY NAME'}
              </Typography>
            )}

          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {menuItems.map(({ text, link, action }) => (
              <Button
                key={text}
                color="inherit"
                onClick={() => (action ? action() : navigate(link))}
              >
                {text}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Phone fontSize="small" />
              <Typography variant="body2">{contactNumber || 'CONTACT NUMBER'}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Email fontSize="small" />
              <Typography variant="body2">{email || 'EMAIL ADDRESS'}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <FacebookSharpIcon
                onClick={() => facebookUrl && window.open(facebookUrl, '_blank')}
                sx={{ cursor: 'pointer' }}
              />
              <LinkedInIcon
                onClick={() => linkedInUrl && window.open(linkedInUrl, '_blank')}
                sx={{ cursor: 'pointer' }}
              />
              <XIcon
                onClick={() => twitterUrl && window.open(twitterUrl, '_blank')}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/patient-login')}
            sx={{ ml: { xs: 0, md: 2 }, minWidth: 135 }}
          >
            Patient Zone
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        >
          <List>
            {menuItems.map(({ text, link, action }) => (
              <ListItem key={text} disablePadding>
                <ListItemButton onClick={() => (action ? action() : navigate(link))}>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <Box p={2}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Contact:
            </Typography>
            <Typography variant="body2">{contactNumber || 'CONTACT NUMBER'}</Typography>
            <Typography variant="body2">{email || 'EMAIL ADDRESS'}</Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
